import {prop, range} from 'ramda';

import {DashboardGridRect} from '../types/DashboardGridRect';
import {DashboardTileId} from '../types/DashboardTileId';
import {
  compareDashboardGridPositions,
  DASHBOARD_GRID_COLUMNS,
  dashboardGridTilesOverlap,
  PositionedDashboardGridTile,
} from './allocateDashboardGrid';
import {DASHBOARD_TILE_SIZES} from './dashboardTileSizes';
import {isDashboardTileFullWidth} from './isDashboardTileFullWidth';

const getBoardBottom = (tiles: PositionedDashboardGridTile[]) =>
  tiles.reduce(
    (bottom, tile) => Math.max(bottom, tile.row + prop(tile.size, DASHBOARD_TILE_SIZES).rows),
    0
  );

// Every free, aligned place inside the current board the active tile could move to on its own.
// The active tile's own cells count as free, so a tall tile may slide into a free row next to it.
export function getDashboardGapSlots(
  tiles: PositionedDashboardGridTile[],
  activeId: DashboardTileId
): DashboardGridRect[] {
  const active = tiles.find((tile) => tile.id === activeId);

  if (active === undefined || isDashboardTileFullWidth(active.size)) {
    return [];
  }

  const {columns, rows} = prop(active.size, DASHBOARD_TILE_SIZES);
  const others = tiles.filter((tile) => tile.id !== activeId);
  const lastRow = getBoardBottom(tiles) - rows;
  const alignedColumns = range(0, DASHBOARD_GRID_COLUMNS / columns).map((index) => index * columns);

  return range(0, lastRow + 1)
    .flatMap((row) => alignedColumns.map((column) => ({...active, column, row})))
    .filter(
      (candidate) =>
        (candidate.column !== active.column || candidate.row !== active.row) &&
        !others.some((tile) => dashboardGridTilesOverlap(candidate, tile))
    )
    .sort(compareDashboardGridPositions)
    .map((candidate) => ({column: candidate.column, row: candidate.row, columns, rows}));
}
